// 회원/병원 인증 API 클라이언트 (브라우저 fetch + credentials)

// auth-api는 항상 브라우저에서 호출되므로 상대 경로
const API_BASE = "/api/v1";

// ── API 에러 ──

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** 인증 필요 (로그인 안 됨) */
  get isUnauthorized() {
    return this.status === 401;
  }
  /** 권한 부족 */
  get isForbidden() {
    return this.status === 403;
  }
  /** 리소스 없음 */
  get isNotFound() {
    return this.status === 404;
  }
  /** 입력값 오류 */
  get isValidation() {
    return this.status === 400 || this.status === 422;
  }
  /** 서버 내부 에러 */
  get isServerError() {
    return this.status >= 500;
  }
  /** 네트워크/연결 에러 */
  get isNetworkError() {
    return this.status === 0;
  }
  /** 요청 제한 초과 */
  get isRateLimited() {
    return this.status === 429;
  }
}

// ── 재시도 설정 ──

const RETRY_CONFIG = {
  maxRetries: 2,
  baseDelayMs: 500,
  // 재시도 가능한 상태 코드 (서버 에러 + 429)
  retryableStatuses: new Set([429, 502, 503, 504]),
} as const;

async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── 타입 ──

export interface User {
  id: number;
  email: string;
  name: string;
  phone?: string;
  user_type: "general" | "hospital" | "admin";
  email_verified: boolean;
  phone_verified: boolean;
  status: string;
  created_at: string;
  last_login_at?: string;
}

export interface HospitalAdmin {
  id: number;
  user_id: number;
  ykiho: string;
  role: string;
  approved_at: string;
}

export interface MeResponse {
  user: User;
  hospital_admin?: HospitalAdmin;
  oauth_provider?: string;
}

export interface HospitalSearchItem {
  ykiho: string;
  name: string;
  sido: string;
  sggu: string;
  address: string;
  hospital_type: string;
  homepage?: string;
  phone?: string;
}

export interface HospitalClaim {
  id: number;
  ykiho: string;
  hospital_name: string;
  verification_method: "document" | "domain_email";
  status: "pending" | "approved" | "rejected";
  created_at: string;
  ocr_match_status?: "auto_pass" | "manual_review";
}

// ── 공통 fetch ──

/** 재시도 가능 여부 판별: GET/HEAD + 재시도 가능 상태 코드 */
function isRetryable(method: string, status: number): boolean {
  const safeMethod = ["GET", "HEAD"].includes(method.toUpperCase());
  return safeMethod && RETRY_CONFIG.retryableStatuses.has(status);
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method || "GET").toUpperCase();
  let lastError: ApiError | null = null;

  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    // 재시도 시 지수 백오프 대기
    if (attempt > 0) {
      await sleep(RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt - 1));
    }

    let res: Response;
    try {
      res = await fetch(`${API_BASE}${path}`, {
        credentials: "include",
        ...init,
        headers: {
          ...(init?.body && !(init.body instanceof FormData)
            ? { "Content-Type": "application/json" }
            : {}),
          ...init?.headers,
        },
      });
    } catch (networkErr) {
      // 네트워크 에러 (오프라인, DNS 실패, 연결 거부 등)
      lastError = new ApiError(
        networkErr instanceof Error ? networkErr.message : "네트워크 연결 실패",
        0,
        "NETWORK_ERROR",
      );
      // GET 요청만 재시도
      if (method === "GET" && attempt < RETRY_CONFIG.maxRetries) continue;
      throw lastError;
    }

    const data = await res.json().catch(() => ({}));

    if (res.ok) {
      return data as T;
    }

    lastError = new ApiError(
      data?.error || data?.message || `API 오류 (${res.status})`,
      res.status,
      data?.code,
      data?.details,
    );

    // 재시도 불가능한 에러는 즉시 throw
    if (!isRetryable(method, res.status)) {
      throw lastError;
    }
  }

  // 모든 재시도 소진
  throw lastError!;
}

// ── 인증 ──

export async function register(
  email: string,
  password: string,
  password_confirm: string,
  name: string,
  agree_terms: boolean,
  agree_marketing: boolean,
) {
  return api<{ user: User; email_auto_verified?: boolean }>("/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, password_confirm, name, agree_terms, agree_marketing }),
  });
}

export async function login(email: string, password: string) {
  return api<{ user: User }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function logout() {
  return api<{ ok: boolean; kakao_logout_url?: string }>("/auth/logout", { method: "POST" });
}

export async function getMe(): Promise<MeResponse | null> {
  try {
    return await api<MeResponse>("/auth/me", { cache: "no-store" });
  } catch {
    return null;
  }
}

// ── 병원 인증 ──

export async function searchHospitals(query: string): Promise<HospitalSearchItem[]> {
  if (query.length < 2) return [];
  return api<HospitalSearchItem[]>(`/hospital/search?q=${encodeURIComponent(query)}`);
}

export async function ocrPreview(formData: FormData) {
  return api<{
    biz_path: string;
    lic_path: string;
    owner_name?: string;
    business_number?: string;
    corp_name?: string;
    doctor_name?: string;
    license_no?: string;
    birthdate?: string;
    names_match?: boolean;
  }>("/hospital/claim/ocr-preview", {
    method: "POST",
    body: formData,
  });
}

export async function submitClaimDocument(formData: FormData) {
  return api<{
    claim: HospitalClaim;
    ocr_match_status: string;
    mismatch_reasons: string[];
    business_ocr: Record<string, string>;
    license_ocr: Record<string, string>;
  }>("/hospital/claim/document", {
    method: "POST",
    body: formData, // multipart
  });
}

export async function requestEmailVerification(ykiho: string, email: string) {
  return api<{
    sent: boolean;
    expires_in?: number;
    warning?: string;
    dev_code?: string;
    hospital_domain?: string;
  }>("/hospital/email-verify/request", {
    method: "POST",
    body: JSON.stringify({ ykiho, email }),
  });
}

export async function confirmEmailVerification(ykiho: string, email: string, code: string) {
  return api<{ claim: HospitalClaim }>("/hospital/email-verify/confirm", {
    method: "POST",
    body: JSON.stringify({ ykiho, email, code }),
  });
}

export async function getMyClaims() {
  return api<{ claims: HospitalClaim[] }>("/hospital/claim/me");
}

// ── 즐겨찾기 + 비교 저장 ──

export interface Favorite {
  id: number;
  user_id: number;
  target_type: "procedure" | "hospital";
  target_id: string;
  created_at: string;
}

export interface SavedComparison {
  id: number;
  user_id: number;
  name?: string;
  procedure_titles: string[];
  created_at: string;
}

export async function addFavorite(targetType: "procedure" | "hospital", targetID: string) {
  return api<{ favorite: Favorite }>("/favorites", {
    method: "POST",
    body: JSON.stringify({ target_type: targetType, target_id: targetID }),
  });
}

export async function removeFavorite(targetType: "procedure" | "hospital", targetID: string) {
  return api<{ ok: boolean }>("/favorites", {
    method: "DELETE",
    body: JSON.stringify({ target_type: targetType, target_id: targetID }),
  });
}

export async function listFavorites(type?: "procedure" | "hospital") {
  const q = type ? `?type=${type}` : "";
  return api<{ favorites: Favorite[] }>(`/favorites/me${q}`);
}

export async function createComparison(name: string, procedureTitles: string[]) {
  return api<{ comparison: SavedComparison }>("/comparisons", {
    method: "POST",
    body: JSON.stringify({ name, procedure_titles: procedureTitles }),
  });
}

export async function listComparisons() {
  return api<{ comparisons: SavedComparison[] }>("/comparisons/me");
}

export async function deleteComparison(id: number) {
  return api<{ ok: boolean }>(`/comparisons/${id}`, { method: "DELETE" });
}

// ── 병원 관리자 대시보드 ──

export interface MyHospitalResponse {
  hospital: HospitalSearchItem;
  visibility: { is_visible: boolean; blocked_reason?: string };
  current_procedures: {
    title: string;
    is_active: boolean;
    is_manual: boolean;
    is_featured: boolean;
  }[];
  pending_changes: {
    id: number;
    change_type: string;
    payload: Record<string, unknown>;
    status: string;
    created_at: string;
  }[];
  stats?: { total_views_7d: number; total_views_30d: number };
}

export async function getMyHospital() {
  return api<MyHospitalResponse>("/my-hospital");
}

export interface ProcedureSearchResult {
  title: string;
  brand_family?: string;
  nature?: string;
  method?: string;
}

export async function searchMyHospitalProcedures(q: string) {
  return api<ProcedureSearchResult[]>(`/my-hospital/search-procedures?q=${encodeURIComponent(q)}`);
}

export async function updateMyHospitalProcedures(
  add: string[],
  remove: string[],
  featured_order?: string[],
) {
  return api<{ change: { id: number; status: string } }>("/my-hospital/procedures", {
    method: "POST",
    body: JSON.stringify({ add, remove, featured_order }),
  });
}

export async function updateMyHospitalInfo(data: {
  phone?: string;
  homepage?: string;
  intro?: string;
}) {
  return api<{ change: { id: number; status: string } }>("/my-hospital/info", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function requestMyHospitalBlock(reason: string) {
  return api<{ change: { id: number; status: string } }>("/my-hospital/block", {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// ── 관리자 ──

export interface AdminClaim extends HospitalClaim {
  user_id: number;
  business_number?: string;
  business_owner_name?: string;
  business_corp_name?: string;
  business_doc_url?: string;
  doctor_name?: string;
  doctor_license_no?: string;
  doctor_license_type?: string;
  license_doc_url?: string;
  verified_email?: string;
  verified_email_domain?: string;
  expected_homepage?: string;
  reviewer_note?: string;
  business_verified?: boolean;
  contact_info?: string;
  phone_verified_name?: string;
  phone_verified_birthdate?: string;
  hospital_addr?: string;
  hospital_tel?: string;
  hospital_sido?: string;
  hospital_sggu?: string;
}

export async function adminListClaims(status = "pending", matchStatus = "") {
  const params = new URLSearchParams({ status });
  if (matchStatus) params.set("match_status", matchStatus);
  return api<{ claims: AdminClaim[] }>(`/admin/claims?${params.toString()}`);
}

export async function adminGetClaim(id: number) {
  return api<{ claim: AdminClaim }>(`/admin/claims/${id}`);
}

export async function adminApproveClaim(id: number, note: string) {
  return api<{ ok: boolean }>(`/admin/claims/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function adminRejectClaim(id: number, note: string) {
  return api<{ ok: boolean }>(`/admin/claims/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

// 변경 신청 검토
export interface AdminChange {
  id: number;
  ykiho: string;
  user_id: number;
  change_type: "procedures_update" | "info_update" | "block_request" | "unblock_request";
  payload: Record<string, unknown>;
  status: "pending" | "approved" | "rejected";
  reviewer_note?: string;
  created_at: string;
  reviewed_at?: string;
}

export async function adminListChanges(status = "pending", type = "") {
  const params = new URLSearchParams({ status });
  if (type) params.set("type", type);
  return api<{ changes: AdminChange[] }>(`/admin/changes?${params.toString()}`);
}

export async function adminApproveChange(id: number, note: string) {
  return api<{ ok: boolean }>(`/admin/changes/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function adminRejectChange(id: number, note: string) {
  return api<{ ok: boolean }>(`/admin/changes/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

// ── 구독/결제 ──

export interface SubscriptionStartParams {
  method: "kakaopay" | "card" | "phone";
}

export interface SubscriptionStartResponse {
  gateway_url: string;
  params: Record<string, string>;
}

export interface SubscriptionInfo {
  hecto_enabled?: boolean;
  phone_enabled?: boolean;
  subscription?: {
    plan: string;
    status: string;
    expires_at?: string;
    [k: string]: unknown;
  };
  plan?: string;
  cancelled?: boolean;
  expires_at?: string;
  [k: string]: unknown;
}

export async function startSubscription(method: SubscriptionStartParams["method"]) {
  return api<SubscriptionStartResponse>("/subscribe/start", {
    method: "POST",
    body: JSON.stringify({ method }),
  });
}

export async function getSubscriptionStatus() {
  return api<SubscriptionInfo>("/subscribe/status", { cache: "no-store" });
}

export async function cancelSubscription() {
  return api<{ ok: boolean; expires_at?: string }>("/subscribe/cancel", { method: "POST" });
}

// ── 리뷰 (시술 후기) ──

export interface ReviewSummary {
  total_reviews: number;
  avg_satisfaction: number;
  price_range?: { min: number; max: number; typical: string };
  pros?: string[];
  cons?: string[];
  tip?: string;
  generated_at?: string;
}

export interface UserReview {
  id: number;
  user_id: number;
  user_name: string;
  procedure_title: string;
  rating: number;
  content: string;
  status: string;
  created_at: string;
}

export interface ReviewsResponse {
  reviews: UserReview[];
  total: number;
  summary: ReviewSummary | null;
}

export async function listReviews(procedureTitle: string, limit = 20) {
  return api<ReviewsResponse>(
    `/reviews?procedure=${encodeURIComponent(procedureTitle)}&limit=${limit}`,
  );
}

export async function submitReview(procedureTitle: string, rating: number, content: string) {
  return api<{ id: number; message?: string }>("/reviews", {
    method: "POST",
    body: JSON.stringify({ procedure_title: procedureTitle, rating, content }),
  });
}

export async function deleteReview(id: number) {
  return api<{ ok: boolean }>(`/reviews/${id}`, { method: "DELETE" });
}
